import React from 'react';
import { Save, Download, Loader2 } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { RoastProfilePDF } from './RoastProfilePDF';
import type { RoastData } from './types';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastFormActionsProps {
  onCancel?: () => void;
  isLoading?: boolean;
  canDownload?: boolean;
  downloadData?: {
    recipeName?: string;
    roastName: string;
    machineName: string;
    beanName: string;
    date: string;
    time: string;
    roastData: RoastData;
    roastType?: string;
    notes?: string;
  };
  readOnly?: boolean;
}

export const RoastFormActions: React.FC<RoastFormActionsProps> = React.memo(({ 
  onCancel, 
  isLoading, 
  canDownload,
  downloadData,
  readOnly = false
}) => {
  const { t } = useLanguage();
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
      </button>

      {canDownload && downloadData && (
        <PDFDownloadLink
          document={<RoastProfilePDF {...downloadData} />}
          fileName={`${t('common.fileName.roastProfilePrefix')}_${downloadData.roastName.replace(/[^a-z0-9]/gi, '_')}_${downloadData.date}.pdf`}
          className="inline-flex items-center px-4 py-2 border border-blue-200 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
        >
          {({ loading }) => (
            <>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              <span>{t('workOrders.actions.downloadProfile')}</span>
            </>
          )}
        </PDFDownloadLink>
      )}

      {!readOnly && (
        <button 
          type="submit" 
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all active:scale-95"
        >
          {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className='w-4 h-4 mr-2'/>}
          <span>{t('forms.actions.save')}</span>
        </button>
      )}
    </div>
  );
});
